(() => {
  const states = new WeakMap();
  const hover = window.matchMedia('(hover: hover)');
  const getState = nav => {
    if (!states.has(nav)) states.set(nav, new Map());
    return states.get(nav);
  };
  const getEntry = (nav, item) => {
    const state = getState(nav);
    if (!state.has(item)) {
      const trigger = item.querySelector(':scope > [aria-controls]');
      const panel = trigger && item.querySelector(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`);
      if (!trigger || !panel) return null;
      state.set(item, { trigger, panel, opening: null, closing: null });
    }
    return state.get(item);
  };
  const occupied = item => (hover.matches && item.matches(':hover')) || item.contains(document.activeElement);
  const clearTimers = entry => {
    clearTimeout(entry.opening);
    clearTimeout(entry.closing);
    entry.opening = null;
    entry.closing = null;
  };
  const hide = (nav, item) => {
    const entry = getEntry(nav, item);
    if (!entry) return;
    clearTimers(entry);
    for (const [child] of getState(nav)) {
      if (child !== item && item.contains(child)) hide(nav, child);
    }
    const wasOpen = entry.trigger.getAttribute('aria-expanded') === 'true';
    entry.trigger.setAttribute('aria-expanded', 'false');
    entry.panel.hidden = true;
    entry.panel.classList.remove('is-closing');
    if (wasOpen) nav.dispatchEvent(new CustomEvent('desktop-nav:dropdown:close', { bubbles: true }));
  };
  const show = (nav, item) => {
    const entry = getEntry(nav, item);
    if (!entry || !item.isConnected) return;
    clearTimers(entry);
    for (const [other] of getState(nav)) {
      if (other !== item && !other.contains(item) && !item.contains(other)) hide(nav, other);
    }
    if (entry.trigger.getAttribute('aria-expanded') === 'true') return;
    entry.panel.classList.remove('is-closing');
    entry.panel.hidden = false;
    entry.trigger.setAttribute('aria-expanded', 'true');
    nav.currentMegaMenu = entry.panel.classList.contains('mega-menu') ? entry.panel : null;
    nav.dispatchEvent(new CustomEvent('desktop-nav:dropdown:open', { bubbles: true }));
  };
  const install = nav => {
    if (nav.dataset.mlHoverReady) return;
    nav.dataset.mlHoverReady = 'true';
    nav.querySelectorAll('.has-dropdown').forEach(item => {
      const entry = getEntry(nav, item);
      if (!entry) return;
      item.addEventListener('mouseenter', () => nav.openDropdown(item));
      item.addEventListener('mouseleave', () => nav.closeDropdown(item));
      entry.panel.addEventListener('mouseenter', () => {
        clearTimeout(entry.closing);
        show(nav, item);
      });
      entry.panel.addEventListener('mouseleave', () => nav.closeDropdown(item));
      item.addEventListener('focusin', () => show(nav, item));
      item.addEventListener('focusout', () => nav.closeDropdown(item));
      entry.trigger.addEventListener('keydown', event => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        show(nav, item);
        entry.panel.querySelector('a[href], button:not([disabled]), input')?.focus();
      });
    });
    nav.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const openItems = [...getState(nav)].filter(([, entry]) => entry.trigger.getAttribute('aria-expanded') === 'true');
      if (!openItems.length) return;
      event.preventDefault();
      event.stopPropagation();
      const [item, entry] = openItems[openItems.length - 1];
      entry.trigger.focus();
      hide(nav, item);
    });
    document.addEventListener('pointerdown', event => {
      if (!nav.contains(event.target)) for (const [item] of getState(nav)) hide(nav, item);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) for (const [item] of getState(nav)) hide(nav, item);
    });
    window.addEventListener('blur', () => {
      for (const [item] of getState(nav)) hide(nav, item);
    });
  };
  customElements.whenDefined('desktop-navigation').then(() => {
    const Navigation = customElements.get('desktop-navigation');
    Navigation.prototype.openDropdown = function (item) {
      const entry = getEntry(this, item);
      if (!entry) return;
      clearTimers(entry);
      if (entry.trigger.getAttribute('aria-expanded') === 'true') return;
      entry.opening = setTimeout(() => show(this, item), hover.matches ? 80 : 0);
    };
    Navigation.prototype.closeDropdown = function (item) {
      const entry = getEntry(this, item);
      if (!entry) return;
      clearTimers(entry);
      entry.closing = setTimeout(() => {
        if (!occupied(item)) hide(this, item);
      }, 320);
    };
    const initialize = () => document.querySelectorAll('desktop-navigation').forEach(install);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
    document.addEventListener('shopify:section:load', initialize);
  });
})();
